/**
 * ⌘K command palette over pages, brands, creatives and tools. Picking a tool opens the Tool Runner
 * (JSON input → runTool with the confirmation flow) — a power-user escape hatch for every capability.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Building2, CornerDownLeft, FileText, Image as ImageIcon, Search, Terminal, Wrench } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { api, errorMessage } from "@/api/client";
import { useRunTool, useTool } from "@/api/hooks";
import type { ToolDescriptor } from "@/api/tools";
import { creativeThumb, pickArray, type Creative } from "@/api/types";
import { cn } from "@/lib/cn";
import { brandPath, useBrandId, useBrands } from "@/state/brand";
import { Kbd, Pill } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Field, Textarea } from "../ui/Form";
import { Modal } from "../ui/Overlay";
import { safeMediaHref } from "@/lib/safe-url";

interface Cmd {
  id: string;
  group: string;
  label: string;
  hint?: string;
  icon: React.ReactNode;
  run: () => void;
}

const PAGES: { label: string; page?: string; path?: string; hint?: string }[] = [
  { label: "Home", page: "home" },
  { label: "Decisions today", page: "decisions" },
  { label: "Ad Monitor", page: "monitor" },
  { label: "Creative Performance", page: "performance" },
  { label: "Creations", page: "creations" },
  { label: "Studio — generate a matrix", page: "studio" },
  { label: "Flows — node-graph editor", page: "flows" },
  { label: "Inspiration", page: "inspiration" },
  { label: "Ideas from competitor sweeps", page: "inspiration?tab=ideas" },
  { label: "Connections", page: "connections" },
  { label: "Scheduler", page: "scheduler" },
  { label: "Brand settings", page: "settings" },
  { label: "Brand assets", page: "assets" },
  { label: "All brands overview", path: "/all" },
  { label: "New chat", path: "/chat" },
  { label: "Brands", path: "/brands" },
  { label: "Onboard a new brand", path: "/brands/new" },
  { label: "Team", path: "/settings/team" },
  { label: "API tokens", path: "/settings/tokens" },
  { label: "Settings", path: "/settings/general" },
  { label: "Audit log", path: "/settings/audit" },
  { label: "Alerts", path: "/settings/alerts" },
  { label: "Jobs", path: "/settings/jobs" },
];

function useToolCatalog(enabled: boolean) {
  return useQuery({
    queryKey: ["tools-catalog"],
    queryFn: async () => pickArray<ToolDescriptor>(await api("/api/tools"), "tools"),
    enabled,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const brandId = useBrandId();
  const { brands } = useBrands();
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const [runner, setRunner] = useState<ToolDescriptor | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const tools = useToolCatalog(open);
  const term = q.trim();
  const creatives = useTool<unknown>("creatives.list", open && brandId && term.length >= 2 ? { brandId, query: term, limit: 6 } : null, { keepPrevious: true });

  useEffect(() => {
    if (open) {
      setQ("");
      setIdx(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const cmds = useMemo<Cmd[]>(() => {
    const s = term.toLowerCase();
    const match = (t: string) => !s || t.toLowerCase().includes(s);
    const out: Cmd[] = [];
    for (const p of PAGES) {
      if (!match(p.label)) continue;
      out.push({
        id: `page:${p.label}`,
        group: "Pages",
        label: p.label,
        icon: <FileText className="size-4" />,
        run: () => nav(p.path ?? brandPath(brandId, p.page!)),
      });
    }
    for (const b of brands) {
      if (!match(b.name)) continue;
      out.push({ id: `brand:${b.id}`, group: "Brands", label: b.name, hint: b.status, icon: <Building2 className="size-4" />, run: () => nav(brandPath(b.id, "home")) });
    }
    if (term.length >= 2) {
      for (const c of pickArray<Creative>(creatives.data, "creatives").slice(0, 6)) {
        const thumb = safeMediaHref(creativeThumb(c));
        out.push({
          id: `creative:${c.id}`,
          group: "Creatives",
          label: c.name,
          hint: String(c.status ?? ""),
          icon: thumb ? <img src={thumb} alt="" className="size-5 rounded object-cover" /> : <ImageIcon className="size-4" />,
          run: () => nav(`${brandPath(brandId, "creations")}?creative=${c.id}`),
        });
      }
    }
    for (const t of tools.data ?? []) {
      if (!match(t.name) && !match(t.title ?? "")) continue;
      out.push({ id: `tool:${t.name}`, group: "Tools", label: t.title ? `${t.title}` : t.name, hint: t.name, icon: <Wrench className="size-4" />, run: () => setRunner(t) });
    }
    if (term) {
      out.push({ id: "ask", group: "Agent", label: `Ask Mimic: “${term}”`, icon: <Terminal className="size-4" />, run: () => nav(`/chat?prompt=${encodeURIComponent(term)}`) });
    }
    return out.slice(0, 60);
  }, [term, brands, brandId, nav, tools.data, creatives.data]);

  useEffect(() => setIdx(0), [term]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${idx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [idx]);

  if (!open && !runner) return null;

  const choose = (c: Cmd | undefined) => {
    if (!c) return;
    c.run();
    if (!c.id.startsWith("tool:")) onClose();
  };

  let lastGroup = "";
  return (
    <>
      {open &&
        createPortal(
          <div className="fixed inset-0 z-[95] flex items-start justify-center px-3 pt-[12vh]">
            <div className="absolute inset-0 animate-fade-in bg-black/60 backdrop-blur-[2px]" onClick={onClose} />
            <div className="relative w-full max-w-[620px] animate-pop-in overflow-hidden rounded-2xl border border-line-strong bg-[#1a1a1c] shadow-pop" role="dialog" aria-label="Command palette">
              <div className="flex items-center gap-3 border-b border-line px-4">
                <Search className="size-4.5 text-muted" />
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search pages, brands, creatives, tools…"
                  className="h-13 flex-1 bg-transparent text-[15px] outline-none placeholder:text-subtle"
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setIdx((i) => Math.min(i + 1, cmds.length - 1));
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setIdx((i) => Math.max(i - 1, 0));
                    } else if (e.key === "Enter") {
                      e.preventDefault();
                      choose(cmds[idx]);
                    } else if (e.key === "Escape") {
                      onClose();
                    }
                  }}
                />
                <Kbd>esc</Kbd>
              </div>
              <div ref={listRef} className="max-h-[52vh] overflow-y-auto p-1.5 scrollbar-thin">
                {cmds.length === 0 ? (
                  <div className="px-3 py-8 text-center text-[13px] text-muted">Nothing matches “{term}”.</div>
                ) : (
                  cmds.map((c, i) => {
                    const header = c.group !== lastGroup ? c.group : null;
                    lastGroup = c.group;
                    return (
                      <div key={c.id}>
                        {header && <div className="px-2.5 pb-1 pt-2.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-subtle">{header}</div>}
                        <button
                          type="button"
                          data-idx={i}
                          onMouseMove={() => setIdx(i)}
                          onClick={() => choose(c)}
                          className={cn("flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-[13.5px]", i === idx ? "bg-elevated" : "hover:bg-hover")}
                        >
                          <span className="grid size-6 shrink-0 place-items-center text-muted">{c.icon}</span>
                          <span className="min-w-0 flex-1 truncate">{c.label}</span>
                          {c.hint && <span className="max-w-[40%] truncate font-mono text-[11px] text-subtle">{c.hint}</span>}
                          {i === idx && <CornerDownLeft className="size-3.5 text-muted" />}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="flex items-center gap-3 border-t border-line px-4 py-2 text-[11px] text-subtle">
                <span className="flex items-center gap-1">
                  <Kbd>↑</Kbd>
                  <Kbd>↓</Kbd> navigate
                </span>
                <span className="flex items-center gap-1">
                  <Kbd>↵</Kbd> open
                </span>
                {tools.error ? <span className="ml-auto">Tool catalog unavailable</span> : <span className="ml-auto">{tools.data?.length ?? 0} tools</span>}
              </div>
            </div>
          </div>,
          document.body,
        )}
      {runner && (
        <ToolRunner
          tool={runner}
          brandId={brandId}
          onClose={() => {
            setRunner(null);
          }}
        />
      )}
    </>
  );
}

function ToolRunner({ tool, brandId, onClose }: { tool: ToolDescriptor; brandId: string | null; onClose: () => void }) {
  const run = useRunTool();
  const props = (tool.inputSchema?.properties ?? {}) as Record<string, unknown>;
  const initial = useMemo(() => {
    const o: Record<string, unknown> = {};
    if ("brandId" in props && brandId) o.brandId = brandId;
    return JSON.stringify(o, null, 2);
  }, [props, brandId]);
  const [text, setText] = useState(initial);
  const [result, setResult] = useState<unknown>(undefined);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setErr(null);
    let input: Record<string, unknown>;
    try {
      input = JSON.parse(text || "{}");
    } catch {
      setErr("Input is not valid JSON");
      return;
    }
    setBusy(true);
    try {
      const r = await run(tool.name, input, { silentError: true });
      if (r !== undefined) setResult(r);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <span className="flex flex-wrap items-center gap-2">
          {tool.title ?? tool.name}
          <code className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[11px] text-muted">{tool.name}</code>
          {tool.risk && <Pill tone={tool.risk === "destructive" ? "danger" : tool.risk === "write" ? "amber" : "muted"} size="xs">{tool.risk}</Pill>}
          {tool.humanOnly && <Pill tone="primary" size="xs">human-only</Pill>}
        </span>
      }
      description={<span className="line-clamp-4 whitespace-pre-wrap">{tool.description}</span>}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()} iconRight={<ArrowRight className="size-4" />}>
            Run tool
          </Button>
        </>
      }
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <Field label="Input (JSON)" hint={Object.keys(props).length ? `Fields: ${Object.keys(props).join(", ")}` : undefined}>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} className="font-mono text-[12px]" spellCheck={false} />
        </Field>
        <Field label="Result">
          <pre className="h-[264px] overflow-auto rounded-lg border border-line bg-surface-3 p-3 font-mono text-[11.5px] leading-relaxed text-muted scrollbar-thin">
            {err ? <span className="text-danger">{err}</span> : result === undefined ? "Run the tool to see its result." : JSON.stringify(result, null, 2)}
          </pre>
        </Field>
      </div>
    </Modal>
  );
}
